import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import { NavBar } from "./components/NavBar";
import { Loader } from "./components/Loader";
import { ContactDrawer } from "./components/ContactDrawer";
import { useHomeMotion } from "./hooks/useHomeMotion";
import { createFocusEngine } from "./audio/focusTrack";
import { Home } from "./pages/Home";
import { AboutPage } from "./pages/AboutPage";

function destPath(to) {
  if (typeof to === "string") return to.split("#")[0] || "/";
  return to.pathname || "/";
}

function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const [theme, setTheme] = useState("day");
  const [loading, setLoading] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const [musicOn, setMusicOn] = useState(false);
  const [musicClock, setMusicClock] = useState({ t: 0, d: 174 });
  const musicRef = useRef(null);
  const curtainRef = useRef(null);
  const curtainTimers = useRef([]);
  const curtainBusy = useRef(false);

  useEffect(() => {
    const saved = window.localStorage.getItem("portfolio-theme");
    if (saved === "night" || saved === "day") setTheme(saved);
    const timer = window.setTimeout(() => setLoading(false), 1800);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("night", theme === "night");
    document.documentElement.classList.toggle("night", theme === "night");
  }, [theme]);

  useHomeMotion(!loading, location.pathname);

  useEffect(() => {
    const engine = createFocusEngine();
    musicRef.current = engine;
    const sync = () => {
      const { audio } = engine;
      setMusicClock({
        t: audio.currentTime || 0,
        d: Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 174,
      });
    };
    const audioEvents = [
      ["timeupdate", sync],
      ["loadedmetadata", sync],
      ["durationchange", sync],
    ];
    audioEvents.forEach(([type, fn]) => engine.audio.addEventListener(type, fn));
    return () => {
      audioEvents.forEach(([type, fn]) => engine.audio.removeEventListener(type, fn));
      engine.dispose();
      musicRef.current = null;
    };
  }, []);

  useEffect(
    () => () => {
      curtainTimers.current.forEach((id) => window.clearTimeout(id));
      document.documentElement.classList.remove("curtain-lock");
    },
    []
  );

  const later = (fn, ms) => {
    const id = window.setTimeout(fn, ms);
    curtainTimers.current.push(id);
    return id;
  };

  const transitionTo = (to) => {
    if (destPath(to) === location.pathname) {
      navigate(to);
      return;
    }
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const curtain = curtainRef.current;
    if (reduce || !curtain || curtainBusy.current) {
      if (!curtainBusy.current) navigate(to);
      return;
    }

    curtainBusy.current = true;
    curtainTimers.current.forEach((id) => window.clearTimeout(id));
    curtainTimers.current = [];
    document.documentElement.classList.add("curtain-lock");

    curtain.classList.remove("is-closing", "is-closed");
    curtain.classList.add("is-active", "is-closing");
    void curtain.offsetWidth;
    curtain.classList.add("is-closed");

    later(() => {
      navigate(to);
      later(() => {
        curtain.classList.remove("is-closing", "is-closed");
        later(() => {
          curtain.classList.remove("is-active", "is-closing", "is-closed");
          document.documentElement.classList.remove("curtain-lock");
          curtainBusy.current = false;
        }, 980);
      }, 350);
    }, 550);
  };

  useEffect(() => {
    const { pathname, hash } = location;
    document.title =
      pathname === "/about"
        ? "About · Sukrit Phaopanich"
        : "Sukrit Phaopanich · AI Product Manager";

    if (hash.startsWith("#work/")) return;

    const id = hash.replace("#", "");
    if (id && document.getElementById(id)) {
      window.requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "instant", block: "start" });
      });
      return;
    }

    window.scrollTo(0, 0);
  }, [location]);

  const toggleMusic = () => {
    const engine = musicRef.current;
    if (!engine) return;
    if (musicOn) {
      engine.stop();
      setMusicOn(false);
      return;
    }
    Promise.resolve(engine.start()).then(
      () => setMusicOn(true),
      () => setMusicOn(false)
    );
  };

  const toggleTheme = () => {
    setTheme((current) => {
      const next = current === "day" ? "night" : "day";
      window.localStorage.setItem("portfolio-theme", next);
      return next;
    });
  };

  return (
    <div className={`App theme-${theme} ${location.pathname === "/about" ? "on-about" : "on-home"}`}>
      <div id="dot-cursor" aria-hidden="true">
        <span className="cur-ring" />
        <span className="cur-core" />
      </div>
      <Loader visible={loading} />
      <div id="route-curtain" ref={curtainRef} aria-hidden="true">
        <div className="rc-scrim" />
        <div className="rc-pane rc-pane--top" />
        <div className="rc-pane rc-pane--bottom" />
      </div>
      <NavBar
        theme={theme}
        onToggleTheme={toggleTheme}
        onOpenContact={() => setContactOpen(true)}
        musicOn={musicOn}
        onToggleMusic={toggleMusic}
        transitionTo={transitionTo}
      />
      <main>
        <Routes>
          <Route
            path="/"
            element={
              <Home
                musicOn={musicOn}
                onToggleMusic={toggleMusic}
                musicClock={musicClock}
                onOpenContact={() => setContactOpen(true)}
              />
            }
          />
          <Route
            path="/about"
            element={
              <AboutPage
                musicOn={musicOn}
                onToggleMusic={toggleMusic}
                onOpenContact={() => setContactOpen(true)}
              />
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <ContactDrawer open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;
